import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { DataPrivacyStatement } from '@/components/DataPrivacyStatement';
import { NavigationBar } from '@/components/NavigationBar';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { SmsConsentCheckboxes, smsConsentDefaultValues } from '@/components/SmsConsentCheckboxes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useProductSelectionStep } from '@/hooks/use-product-selection-step';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { trackContactInfoCollected } from '@/lib/analytics';
import { type ForMeFormData, forMeFormId, forMeFormSchema } from '@/types/user-profile.type';

import { ShippingAddressFormFields } from '../ShippingAddressFormFields';

export const ForMe = () => {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const {
    contactPhoneNumber,
    contactEmail,
    contactAddressLine1,
    contactAddressLine2,
    contactCity,
    contactState,
    contactZip,
  } = useSessionStore();

  const form = useForm<ForMeFormData>({
    resolver: zodResolver(forMeFormSchema),
    defaultValues: {
      phoneNumber: contactPhoneNumber,
      email: contactEmail,
      addressLine1: contactAddressLine1,
      addressLine2: contactAddressLine2,
      city: contactCity,
      state: contactState,
      zip: contactZip,
      ...smsConsentDefaultValues,
    },
  });

  const { nextStep } = useProductSelectionStep();

  const onSubmit = async (data: ForMeFormData) => {
    const { updateState } = useSessionStore.getState();

    updateState({
      contactPhoneNumber: data.phoneNumber,
      contactEmail: data.email,
      contactAddressLine1: data.addressLine1,
      contactAddressLine2: data.addressLine2,
      contactCity: data.city,
      contactState: data.state,
      contactZip: data.zip,
    });

    trackContactInfoCollected({
      primaryContactEmail: data.email,
      primaryContactPhoneNumber: data.phoneNumber,
      primaryContactPhoneNumberVerified: false,
      patientRecipient: 'me',
    });

    // Auto-save session (non-blocking)
    saveSession();

    navigate({ to: nextStep });
  };

  const handleBack = () => {
    navigate({ to: '/insurance/benefits' });
  };

  return (
    <section>
      <NavigationBar onBack={handleBack} backLabel="Results" />
      <Card>
        <CardHeader>
          <CardTitle>First, let's get your contact info</CardTitle>
        </CardHeader>

        <CardContent>
          <FormProvider {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              id={forMeFormId}
              className="space-y-8 sm:space-y-12"
            >
              <FieldGroup>
                <div className="space-y-7">
                  <Controller
                    name="phoneNumber"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const inputId = `${forMeFormId}-phoneNumber`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={inputId}>Mobile phone</FieldLabel>
                          <PhoneNumberInput
                            {...field}
                            id={inputId}
                            aria-invalid={fieldState.invalid}
                          />
                          {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    name="email"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const inputId = `${forMeFormId}-email`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={inputId}>Email</FieldLabel>
                          <Input
                            {...field}
                            id={inputId}
                            type="email"
                            aria-invalid={fieldState.invalid}
                            placeholder="name@youremail.com"
                            inputMode="email"
                          />
                          {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                        </Field>
                      );
                    }}
                  />

                  <ShippingAddressFormFields formId={forMeFormId} />
                </div>
              </FieldGroup>

              <SmsConsentCheckboxes control={form.control} formId={forMeFormId} />

              <Button type="submit" className="w-full sm:w-auto sm:min-w-[186px]">
                Continue
              </Button>
            </form>
          </FormProvider>
        </CardContent>
      </Card>

      <DataPrivacyStatement />
    </section>
  );
};
